import {
  type BizcycleProject,
  BusinessSteps,
  type BusinessStep,
  type JiraIssue,
  routes,
} from '@pmai/contracts';
import { InlineSelect } from '@pmai/ui';
import { useTranslation } from 'react-i18next';
import { useApiMutation } from '../../shared/api/hooks';
import { useEnumLabel } from '../../shared/i18n';
import { stepOf } from './bizcycle-model';

/** Saving a task's step, project or "burning" (FR-BIZ-008, FR-BIZ-015) — refreshes the wheel. */
export function useIssueUpdate() {
  return useApiMutation(routes.bizcycle.update, { invalidates: [routes.bizcycle.issues] });
}

/** "Posms" and "Projekts" of a task; a change is saved at once and marked manual (FR-BIZ-008). */
export function IssueControls({
  issue,
  projects,
  disabled,
}: {
  issue: JiraIssue;
  projects: readonly BizcycleProject[];
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const update = useIssueUpdate();
  const save = (body: { step?: BusinessStep; projectId?: string }) =>
    update.mutate({ params: { key: issue.key }, body });
  return (
    <>
      <InlineSelect
        label={t('bizcycle.project', { key: issue.key })}
        value={issue.projectId ?? ''}
        disabled={disabled}
        onChange={(e) => save({ projectId: e.target.value })}
        options={[
          { value: '', label: t('bizcycle.unmapped') },
          ...projects.map((p) => ({ value: p.id, label: p.name })),
        ]}
      />
      <InlineSelect
        label={t('bizcycle.step', { key: issue.key })}
        value={stepOf(issue)}
        disabled={disabled}
        onChange={(e) => save({ step: e.target.value as BusinessStep })}
        options={BusinessSteps.options.map((o) => ({ value: o.code, label: `${o.code} ${label(o)}` }))}
      />
    </>
  );
}
